import { TutorialLayout, CodePreview } from '../../../../shared/components';
import { tutorialLevels } from '../../../../shared/config/tutorials';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Card, CardContent } from '@voilajsx/uikit/card';
import { Info } from 'lucide-react';

const AdminLayoutTutorial = () => {
  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">AdminLayout</h1>
          <p className="text-lg text-muted-foreground">
            AdminLayout is for dashboards, admin panels, and internal applications with sidebar navigation.
          </p>
        </div>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Visual Structure</h2>

          <Card>
            <CardContent className="pt-6">
              <div className="border border-border rounded-lg overflow-hidden flex">
                <div className="bg-primary/20 w-48 min-h-[250px] p-3 border-r border-border">
                  <div className="text-sm font-medium mb-4">Sidebar</div>
                  <div className="space-y-2">
                    <div className="bg-primary/30 rounded p-2 text-xs">Dashboard</div>
                    <div className="bg-background rounded p-2 text-xs text-muted-foreground">Users</div>
                    <div className="bg-background rounded p-2 text-xs text-muted-foreground">Settings</div>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="bg-primary/10 p-3 border-b border-border text-sm font-medium">
                    Header (breadcrumb, user menu)
                  </div>
                  <div className="p-4 min-h-[200px] flex items-center justify-center text-muted-foreground">
                    Main Content Area
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Basic Usage</h2>

          <CodePreview
            title="Simple AdminLayout"
            code={`import { AdminLayout } from '@voilajsx/uikit/admin-layout';

function Dashboard() {
  return (
    <AdminLayout
      sidebar={<Sidebar />}
      header={<AdminHeader />}
    >
      <h1 className="text-2xl font-bold mb-6">Dashboard</h1>
      <div className="grid md:grid-cols-3 gap-4">
        {/* Dashboard cards */}
      </div>
    </AdminLayout>
  );
}`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Props Reference</h2>

          <div className="bg-muted/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 font-semibold">Prop</th>
                  <th className="text-left p-3 font-semibold">Type</th>
                  <th className="text-left p-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">sidebar</td>
                  <td className="p-3 text-muted-foreground">ReactNode</td>
                  <td className="p-3 text-muted-foreground">Sidebar navigation component</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">header</td>
                  <td className="p-3 text-muted-foreground">ReactNode</td>
                  <td className="p-3 text-muted-foreground">Top header component</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">sidebarWidth</td>
                  <td className="p-3 text-muted-foreground">number</td>
                  <td className="p-3 text-muted-foreground">Sidebar width in pixels (default: 256)</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">collapsible</td>
                  <td className="p-3 text-muted-foreground">boolean</td>
                  <td className="p-3 text-muted-foreground">Allow sidebar collapse</td>
                </tr>
                <tr>
                  <td className="p-3 font-mono">defaultCollapsed</td>
                  <td className="p-3 text-muted-foreground">boolean</td>
                  <td className="p-3 text-muted-foreground">Start with collapsed sidebar</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Sidebar Component</h2>

          <CodePreview
            title="Sidebar Example"
            code={`import { Link, useLocation } from 'react-router-dom';
import { Home, Users, Settings, BarChart } from 'lucide-react';

function Sidebar() {
  const location = useLocation();

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/dashboard/analytics', label: 'Analytics', icon: BarChart },
    { path: '/dashboard/users', label: 'Users', icon: Users },
    { path: '/dashboard/settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="h-full bg-card border-r border-border">
      {/* Logo */}
      <div className="h-16 flex items-center px-6 border-b border-border">
        <span className="font-bold text-lg">Admin</span>
      </div>

      {/* Navigation */}
      <nav className="p-4 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={\`flex items-center gap-3 px-3 py-2 rounded-md transition-colors \${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }\`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Admin Header</h2>

          <CodePreview
            title="Header with Breadcrumb"
            code={`import { Button } from '@voilajsx/uikit/button';
import { Avatar, AvatarFallback, AvatarImage } from '@voilajsx/uikit/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@voilajsx/uikit/dropdown-menu';
import { Bell, ChevronRight } from 'lucide-react';

function AdminHeader() {
  return (
    <header className="h-16 border-b border-border bg-background px-6 flex items-center justify-between">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Dashboard</span>
        <ChevronRight className="w-4 h-4 text-muted-foreground" />
        <span>Analytics</span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon">
          <Bell className="w-5 h-5" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger>
            <Avatar className="w-8 h-8">
              <AvatarImage src="/avatar.png" />
              <AvatarFallback>JD</AvatarFallback>
            </Avatar>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem>Profile</DropdownMenuItem>
            <DropdownMenuItem>Settings</DropdownMenuItem>
            <DropdownMenuItem>Logout</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}`}
            language="tsx"
          />
        </section>

        <Alert>
          <Info className="w-4 h-4" />
          <AlertDescription>
            On mobile screens, AdminLayout automatically converts the sidebar to a slide-out drawer accessible via a hamburger menu.
          </AlertDescription>
        </Alert>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Collapsible Sidebar</h2>

          <CodePreview
            title="With Collapse Toggle"
            code={`<AdminLayout
  sidebar={<Sidebar />}
  header={<AdminHeader />}
  collapsible={true}
  defaultCollapsed={false}
>
  {/* Content */}
</AdminLayout>

// In your Sidebar component, use the collapse context:
import { useSidebar } from '@voilajsx/uikit/admin-layout';

function Sidebar() {
  const { collapsed, toggle } = useSidebar();

  return (
    <div className={collapsed ? 'w-16' : 'w-64'}>
      <Button onClick={toggle}>
        {collapsed ? '→' : '←'}
      </Button>
      {/* Show icons only when collapsed */}
    </div>
  );
}`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Complete Example</h2>

          <CodePreview
            title="Dashboard with AdminLayout"
            code={`import { AdminLayout } from '@voilajsx/uikit/admin-layout';
import { Card, CardContent, CardHeader, CardTitle } from '@voilajsx/uikit/card';

function DashboardPage() {
  return (
    <AdminLayout sidebar={<Sidebar />} header={<AdminHeader />}>
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Dashboard</h1>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">
                Total Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">12,345</div>
            </CardContent>
          </Card>
          {/* More stat cards... */}
        </div>

        {/* Charts, tables, etc. */}
      </div>
    </AdminLayout>
  );
}`}
            language="tsx"
          />
        </section>
      </div>
    </TutorialLayout>
  );
};

export default AdminLayoutTutorial;
